import { useState, type FormEvent } from "react";
import { useI18n } from "../i18n";
import { closure } from "../modules";
import { createCompany } from "../data";
import { ModuleGrid } from "../components/ModuleGrid";
import { LangSwitch } from "../components/LangSwitch";
import type { Module, Preset } from "../types";

interface Props {
  catalog: Module[];
  presets: Preset[];
  onCreated: (companyId: string) => void;
  onSignOut: () => void;
}

type Step = 0 | 1 | 2;

export function Setup({ catalog, presets, onCreated, onSignOut }: Props) {
  const { t, pick, lang } = useI18n();
  const [step, setStep] = useState<Step>(0);
  const [name, setName] = useState("");
  const [nameAr, setNameAr] = useState("");
  const [language, setLanguage] = useState<"en" | "ar">(lang);
  const [preset, setPreset] = useState(presets[0]?.key ?? "basic_startup");
  const [modules, setModules] = useState<Set<string>>(() => closure(presets[0]?.modules ?? [], catalog));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function choosePreset(p: Preset) {
    setPreset(p.key);
    setModules(closure(p.modules, catalog));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (step === 0) {
      if (!name.trim()) return setError(t("nameRequired"));
      setError(null);
      return setStep(1);
    }
    if (step === 1) return setStep(2);
    setBusy(true);
    setError(null);
    try {
      const id = await createCompany({ name, nameAr, language, preset, modules, catalog });
      onCreated(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errorGeneric"));
      setBusy(false);
    }
  }

  const steps = [t("stepCompany"), t("stepPreset"), t("stepModules")];

  return (
    <main className="setup">
      <header className="setup-top">
        <img src="/peoplecore-logo.svg" alt="PeopleCore" height={32} />
        <div className="row">
          <LangSwitch />
          <button type="button" className="btn secondary small" onClick={onSignOut}>
            {t("signOut")}
          </button>
        </div>
      </header>
      <form className="setup-card" onSubmit={submit} noValidate>
        <h1>{t("setupTitle")}</h1>
        <ol className="stepper">
          {steps.map((s, i) => (
            <li key={s} className={i === step ? "current" : i < step ? "done" : ""} aria-current={i === step ? "step" : undefined}>
              <span className="step-num">{i + 1}</span>
              {s}
            </li>
          ))}
        </ol>

        {step === 0 && (
          <div className="stack">
            <label className="field">
              <span>{t("companyName")}</span>
              <input id="company-name" value={name} onChange={(e) => setName(e.target.value)} dir="ltr" required autoFocus />
            </label>
            <label className="field">
              <span>
                {t("companyNameAr")} <em>{t("optional")}</em>
              </span>
              <input id="company-name-ar" value={nameAr} onChange={(e) => setNameAr(e.target.value)} dir="rtl" lang="ar" />
            </label>
            <div className="field-row">
              <label className="field">
                <span>{t("country")}</span>
                <select id="country" value="EG" disabled>
                  <option value="EG">{lang === "ar" ? "مصر" : "Egypt"}</option>
                </select>
              </label>
              <label className="field">
                <span>{t("defaultLanguage")}</span>
                <select id="language" value={language} onChange={(e) => setLanguage(e.target.value as "en" | "ar")}>
                  <option value="en">{t("english")}</option>
                  <option value="ar">{t("arabic")}</option>
                </select>
              </label>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="stack">
            <p className="muted">{t("presetHelp")}</p>
            <div className="preset-grid" role="radiogroup">
              {presets.map((p) => (
                <label key={p.key} className={`preset${preset === p.key ? " on" : ""}`}>
                  <input type="radio" name="preset" id={`preset-${p.key}`} checked={preset === p.key} onChange={() => choosePreset(p)} />
                  <span className="preset-name">{pick(p.name_en, p.name_ar)}</span>
                  <span className="preset-fit">{p.fit}</span>
                  <span className="preset-count">
                    {closure(p.modules, catalog).size} {t("modulesOn")}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="stack">
            <p className="muted">{t("modulesHelp")}</p>
            <ModuleGrid catalog={catalog} enabled={modules} onChange={setModules} disabled={busy} />
          </div>
        )}

        {error && <p className="alert danger" role="alert">{error}</p>}

        <div className="actions">
          {step > 0 && (
            <button type="button" className="btn secondary" onClick={() => setStep((step - 1) as Step)} disabled={busy}>
              {t("back")}
            </button>
          )}
          <button className={`btn ${step === 2 ? "spark" : "primary"}`} disabled={busy}>
            {busy ? t("creating") : step === 2 ? t("createCompany") : t("next")}
          </button>
        </div>
      </form>
    </main>
  );
}
